import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Megaphone, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full-screen blocking gate: while active announcements are unconfirmed the
// staff member cannot interact with ANYTHING in the portal. Banners are shown
// one at a time — confirming one reveals the next until all are read.
export const AnnouncementGate = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [totalToRead, setTotalToRead] = useState(0);
  const [readCount, setReadCount] = useState(0);
  const [confirming, setConfirming] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/me/announcement`);
      if (res.data && res.data.id) {
        setAnnouncement(res.data);
        setTotalToRead((prev) => prev || (res.data.pending_total || 1));
        return true;
      }
      setAnnouncement(null);
      return false;
    } catch (_e) {
      // fail open — never lock staff out on network errors
      setAnnouncement(null);
      return false;
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  if (!announcement) return null;

  const position = Math.min(readCount + 1, totalToRead || 1);

  const confirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/users/me/announcement/${announcement.id}/confirm`);
      const next = readCount + 1;
      setReadCount(next);
      const hasMore = await fetchPending();
      if (hasMore) {
        toast.success(`Confirmed — next announcement (${Math.min(next + 1, totalToRead)} of ${totalToRead})`);
      } else {
        toast.success('Thank you — all announcements confirmed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not confirm, please try again');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
      data-testid="announcement-gate"
    >
      <div className="w-full max-w-lg max-h-[90vh] bg-card border-2 border-primary rounded-sm shadow-2xl overflow-hidden flex flex-col">
        <div className="bg-primary px-6 py-4 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <Megaphone className="w-6 h-6 text-primary-foreground" />
            <span className="font-barlow text-lg tracking-wider uppercase text-primary-foreground">
              Important announcement
            </span>
          </div>
          {(totalToRead > 1) && (
            <span className="text-xs font-semibold text-primary-foreground bg-black/20 px-2 py-1 rounded-sm" data-testid="announcement-progress">
              {position} of {totalToRead}
            </span>
          )}
        </div>
        {/* Scrollable body — long messages scroll here so the Confirm button
            below always stays visible on small screens. */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          <h2 className="text-lg font-semibold" data-testid="announcement-title">{announcement.title}</h2>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="announcement-message">
            {announcement.message}
          </p>
        </div>
        {/* Pinned footer with the Confirm button — always on screen */}
        <div className="p-4 sm:p-6 pt-3 border-t border-border shrink-0 space-y-3 bg-card">
          <p className="text-xs text-muted-foreground">
            You must read and confirm {totalToRead > 1 ? 'each announcement' : 'this announcement'} before using the portal.
          </p>
          <Button
            onClick={confirm}
            disabled={confirming}
            className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider"
            data-testid="announcement-confirm-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {confirming ? 'Confirming…' : 'Confirm & Understood'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementGate;
